/**
 * Qual item de `negociacao.url_termo` é "o termo desta negociação".
 *
 * A regra ingênua ("o marcado `validado`, senão o último do array") produziu o bug
 * da negociação 3856 (Big Bag Nogueira): o 1º termo foi marcado `validado` quando o
 * cliente assinou, depois vieram mais dois termos e o cliente assinou o TERCEIRO —
 * mas `validado` é gravado uma vez e ninguém o move. Resultado: a análise cadastral
 * recebeu o termo errado e o histórico exibia "Assinado" no documento errado.
 *
 * `validado` sozinho não pode decidir porque é uma marca ANTIGA que nada invalida
 * quando um termo novo é emitido. A data de assinatura, sim, é um fato: o termo que
 * vale é o ÚLTIMO que o cliente efetivamente assinou.
 *
 * Ordem de decisão:
 *   1. maior `data_assinatura` — o último ato real do cliente;
 *   2. `validado` — escolha manual do admin em termos antigos, sem data por termo;
 *   3. mais recente por `data_criacao` — nada foi assinado ainda.
 *
 * O passo 2 continua honrando o DefinirTermoValidoModal: ao validar um termo ele
 * limpa `data_assinatura` dos demais, então o escolhido é o único com data e vence
 * já no passo 1.
 */

/** Campos de um item de `url_termo` que interessam para decidir e exibir assinatura. */
export interface TermoAssinavel {
    validado?: boolean;
    /** Quando o cliente assinou no Autentique (ISO). Ausente = sem assinatura conhecida. */
    data_assinatura?: string;
    /** Quando o termo foi gerado (ISO). */
    data_criacao?: string;
    /** false = assinou, mas a biometria (SERPRO) ainda não voltou — o Autentique
     * mostra "Aguardando verificação" e não gera o PDF assinado. */
    assinatura_verificada?: boolean;
    /** Quando o servidor de e-mail do cliente confirmou a entrega do convite. */
    email_entregue_em?: string;
    /** Quando o servidor de e-mail do cliente RECUSOU o convite (antispam, caixa
     * inexistente). Preenchido = o cliente nunca recebeu o link de assinatura, e
     * "não assinado" não é escolha dele. Ausente nos termos anteriores a 08/2026 e
     * em todo documento entregue por link — nesses casos não se sabe, e "não sei"
     * nunca vira alerta. */
    email_recusado_em?: string;
    /** Mensagem devolvida pelo servidor na recusa (ex.: o `550 ... spam ...`). */
    email_recusa_motivo?: string;
}

/** true quando o Autentique registrou que o convite deste termo foi recusado pelo
 * servidor do cliente. Deliberadamente separado de `StatusTermo`: entrega e
 * assinatura são fatos ortogonais, e fundir os dois quebraria a precedência de
 * "assinado" (quem assinou pelo link do WhatsApp assinou, tendo o e-mail chegado
 * ou não). */
export function emailNaoEntregue(termo: TermoAssinavel | null | undefined): boolean {
    return !!termo?.email_recusado_em;
}

/** Timestamp de um ISO, ou null quando ausente/inválido. */
const ts = (valor?: string): number | null => {
    if (!valor) return null;
    const n = Date.parse(valor);
    return Number.isNaN(n) ? null : n;
};

/** true quando o termo tem assinatura do cliente registrada. */
export function termoEstaAssinado(termo: TermoAssinavel | null | undefined): boolean {
    return ts(termo?.data_assinatura) !== null;
}

/**
 * O último termo efetivamente assinado pelo cliente, ou null se nenhum foi assinado.
 * É este que deve chegar à análise cadastral e ser rotulado "assinado" na tela.
 */
export function getUltimoTermoAssinado<T extends TermoAssinavel>(
    urlTermo: T[] | null | undefined
): T | null {
    if (!urlTermo || urlTermo.length === 0) return null;

    let melhor: T | null = null;
    let melhorTs = -Infinity;
    urlTermo.forEach(termo => {
        const t = ts(termo?.data_assinatura);
        if (t === null) return;
        // >= mantém o mais adiantado no array quando há empate de data, que é a
        // ordem em que os termos foram emitidos.
        if (t >= melhorTs) {
            melhorTs = t;
            melhor = termo;
        }
    });
    return melhor;
}

/**
 * O termo operante da negociação. Ver a regra de decisão no topo do arquivo.
 */
export function getTermoValido<T extends TermoAssinavel>(
    urlTermo: T[] | null | undefined
): T | null {
    if (!urlTermo || urlTermo.length === 0) return null;

    const assinado = getUltimoTermoAssinado(urlTermo);
    if (assinado) return assinado;

    const validado = urlTermo.find(t => t?.validado);
    if (validado) return validado;

    // Sem assinatura e sem escolha manual: o mais recente por data_criacao. O
    // fallback para o fim do array cobre termos antigos sem data_criacao válida.
    let maisRecente: T | null = null;
    let maisRecenteTs = -Infinity;
    urlTermo.forEach(termo => {
        const t = ts(termo?.data_criacao);
        if (t !== null && t >= maisRecenteTs) {
            maisRecenteTs = t;
            maisRecente = termo;
        }
    });
    return maisRecente ?? urlTermo[urlTermo.length - 1] ?? null;
}

/** Estado de assinatura de um termo, para rótulo e ordenação na tela. */
export type StatusTermo = 'assinado' | 'aguardando_verificacao' | 'nao_assinado';

export function getStatusTermo(termo: TermoAssinavel | null | undefined): StatusTermo {
    if (!termoEstaAssinado(termo)) return 'nao_assinado';
    // `assinatura_verificada` só existe em termos sincronizados depois de 08/2026;
    // ausente significa "não sabemos", e nesse caso a assinatura vale como concluída
    // — era o comportamento anterior e é o caso da esmagadora maioria dos termos.
    return termo?.assinatura_verificada === false ? 'aguardando_verificacao' : 'assinado';
}

/**
 * Termos ordenados do mais novo para o mais antigo, cada um com o que a tela precisa
 * saber: se está assinado, se é o último assinado e se é o termo operante (o que foi
 * para a análise cadastral).
 */
export function descreverHistoricoTermos<T extends TermoAssinavel>(
    urlTermo: T[] | null | undefined
): Array<{
    termo: T;
    /** 1 = primeiro termo emitido, na ordem cronológica de emissão. */
    ordemEmissao: number;
    status: StatusTermo;
    ehUltimoAssinado: boolean;
    /** O termo operante — o que a análise cadastral recebe. */
    ehTermoValido: boolean;
}> {
    if (!urlTermo || urlTermo.length === 0) return [];

    const ultimoAssinado = getUltimoTermoAssinado(urlTermo);
    const valido = getTermoValido(urlTermo);

    const cronologico = urlTermo
        .map((termo, idxOriginal) => ({ termo, idxOriginal }))
        .sort((a, b) => {
            const ta = ts(a.termo?.data_criacao);
            const tb = ts(b.termo?.data_criacao);
            // Sem data válida, preserva a ordem do array (ordem de emissão).
            if (ta === null || tb === null) return a.idxOriginal - b.idxOriginal;
            return ta - tb;
        });

    return cronologico
        .map(({ termo }, i) => ({
            termo,
            ordemEmissao: i + 1,
            status: getStatusTermo(termo),
            ehUltimoAssinado: !!ultimoAssinado && termo === ultimoAssinado,
            ehTermoValido: !!valido && termo === valido,
        }))
        .reverse();
}
